import Head from 'next/head';
import React from 'react';
import { get } from 'lodash';

import * as Sentry from '@sentry/browser';
import { getDataFromTree } from '@apollo/react-ssr';
import { isBrowser } from '@zapier/common-utils';

import createApolloClient from './createApolloClient';

const is3xxStatus = status => status && status > 299 && status < 400;
const is4xxStatus = status => status && status > 399 && status < 500;
const is5xxStatus = status => status && status > 499 && status < 600;

export default (ComposedComponent, config) => {
  const { isProduction } = config;

  return class Apollo extends React.Component<*> {
    static displayName = 'withApolloClient(ComposedComponent)';

    static async getInitialProps(context) {
      const { Component, router } = context;

      let appProps = {};

      // Initial serverState with apollo (empty)
      let serverState = {
        apollo: {},
      };

      const apolloClient = createApolloClient(serverState.apollo, config);

      context.ctx.apolloClient = apolloClient;

      // If a component has a `getInitialProps` function, then perform it.
      // This ensures that if we have to redirect, we do it now vs. attempting
      // to render the tree and further waste processing power.
      if (ComposedComponent.getInitialProps) {
        appProps = await ComposedComponent.getInitialProps(context, {
          apolloClient,
          router,
        });
      }

      const statusCode = get(context, ['ctx', 'res', 'statusCode']);

      // Only attempt to getDataFromTree on the server-side, on the client
      // side we will rely on our components to grab this data. This ensures
      // that navigation is not delayed. Otherwise this would attempt to
      // pre-fetch all the data before navigating.
      if (!isBrowser()) {
        // If we encounter a `3xx`, `4xx`, or `5xx` status code during the
        // `getInitialProps` function, then we should avoid continuing to
        // `getDataFromTree` as we have an error. Page components can set a
        // `statusCode` in their `getInitialProps` if they encounter problems
        // such as missing or incomplete data requirements required to render
        // the page.
        if (
          is3xxStatus(statusCode) ||
          is4xxStatus(statusCode) ||
          is5xxStatus(statusCode)
        ) {
          return {
            ...appProps,
            serverState: {
              apollo: apolloClient.cache.extract(),
            },
            statusCode,
          };
        }

        try {
          // This LOC will go through the React component tree and pull out any
          // Apoollo GraphQL queries and make those requests. If an error occurs
          // during this process then the page will be sent to the client
          // without any data pre-hydrated.
          // When the page is then received on the client-side, the client will
          // re-request all the data required for the page and hydrate the data
          // itself.
          await getDataFromTree(
            <ComposedComponent
              {...appProps}
              apolloClient={apolloClient}
              Component={Component}
              router={router}
              serverState={serverState}
            />
          );
        } catch (error) {
          // In production-like environments, this error will be reported to Sentry.
          // See: createApolloClient.js
          console.error(
            JSON.stringify({
              message: 'Error while running `getDataFromTree`',
              error: error.stack || error,
            })
          );

          // Let's inform developers that they have implemented an error which
          // could lead to SSR issues in production. Shipping an SSR issue into
          // production can cause SEO issues so we want to avoid this at all
          // cost.
          if (isProduction) {
            throw error;
          }

          // Prevent Apollo Client GraphQL errors from crashing SSR.
          // Handle them in components via the data.error prop:
          // http://dev.apollodata.com/react/api-queries.html#graphql-query-data-error
          Sentry.captureException(error);
        }

        // getDataFromTree does not call componentWillUnmount
        // head side effect therefore need to be cleared manually
        Head.rewind();
      }

      // Extract query data from the Apollo store
      serverState = {
        apollo: apolloClient.cache.extract(),
      };

      return {
        ...appProps,
        serverState,
        statusCode,
      };
    }

    apolloClient = null;

    constructor(props) {
      super(props);
      this.apolloClient = createApolloClient(props.serverState.apollo, config);
    }

    render() {
      return (
        <ComposedComponent {...this.props} apolloClient={this.apolloClient} />
      );
    }
  };
};
